/**
 * «Cambió el plano» (OPUS ci, idea 4): cliente de las revisiones de la hoja de un plano. Solo staff (las rutas
 * lo exigen). `fileId` es el archivo que la hoja tiene HOY: el viejo antes de aplicar, el nuevo después.
 *
 * Los errores llegan como `ApiClientError` con `.code` y `.message` ya redactado para la persona (409
 * MISMO_ARCHIVO, REVISION_DESACTUALIZADA, IA_EN_CURSO…; ver `routes/planoRevisiones.ts`).
 */
import type {
  AplicarPlanoRevisionResponse,
  ComparacionDePlanos,
  CrearPlanoRevisionRequest,
  CrearPlanoRevisionResponse,
  GuardarComparacionRequest,
  GuardarComparacionResponse,
  ListarPlanoRevisionesResponse,
  ObtenerPlanoRevisionResponse,
  PlanoRevisionDetalle,
  PlanoRevisionResumen,
  RevertirPlanoRevisionResponse,
  UsarPropuestaDeRevisionResponse,
} from '@supernova/shared';
import { apiFetch } from './client.js';

const base = (projectId: string, fileId: string) => `/api/v1/projects/${projectId}/planos/${fileId}/revisiones`;

/** Sube la revisión nueva (el archivo ya está en el proyecto). 201. */
export async function crearRevisionPlano(projectId: string, fileId: string, archivoNuevoId: string): Promise<PlanoRevisionResumen> {
  const r = await apiFetch<CrearPlanoRevisionResponse>(base(projectId, fileId), {
    method: 'POST',
    body: { archivoNuevoId } satisfies CrearPlanoRevisionRequest,
    acceptStatus: [201] as number[],
  });
  return r.revision;
}

/** Las revisiones de la hoja + la huella y los renglones con que se compara (en una sola lectura). */
export async function listarRevisionesPlano(projectId: string, fileId: string): Promise<ListarPlanoRevisionesResponse> {
  return await apiFetch<ListarPlanoRevisionesResponse>(base(projectId, fileId));
}

/** Una revisión con su comparación guardada. */
export async function obtenerRevisionPlano(projectId: string, fileId: string, revisionId: string): Promise<PlanoRevisionDetalle> {
  const r = await apiFetch<ObtenerPlanoRevisionResponse>(`${base(projectId, fileId)}/${revisionId}`);
  return r.revision;
}

/** Guarda la comparación hecha en el navegador, con la huella de la hoja que se comparó. */
export async function guardarComparacionPlano(
  projectId: string,
  fileId: string,
  revisionId: string,
  comparacion: ComparacionDePlanos,
  huellaHoja: string,
): Promise<PlanoRevisionResumen> {
  const r = await apiFetch<GuardarComparacionResponse>(`${base(projectId, fileId)}/${revisionId}/comparacion`, {
    method: 'PUT',
    body: { comparacion, huellaHoja } satisfies GuardarComparacionRequest,
  });
  return r.revision;
}

export async function aplicarRevisionPlano(projectId: string, fileId: string, revisionId: string): Promise<AplicarPlanoRevisionResponse> {
  return await apiFetch<AplicarPlanoRevisionResponse>(`${base(projectId, fileId)}/${revisionId}/aplicar`, { method: 'POST' });
}

export async function revertirRevisionPlano(projectId: string, fileId: string, revisionId: string): Promise<RevertirPlanoRevisionResponse> {
  return await apiFetch<RevertirPlanoRevisionResponse>(`${base(projectId, fileId)}/${revisionId}/revertir`, { method: 'POST' });
}

/** «Usar la cantidad nueva» de un renglón que quedó para revisar (solo con la revisión aplicada). */
export async function usarPropuestaRevision(
  projectId: string,
  fileId: string,
  revisionId: string,
  renglonId: string,
): Promise<UsarPropuestaDeRevisionResponse> {
  return await apiFetch<UsarPropuestaDeRevisionResponse>(`${base(projectId, fileId)}/${revisionId}/renglones/${renglonId}/usar-propuesta`, {
    method: 'POST',
  });
}

/** Descarta una revisión sin aplicar. 204. */
export async function descartarRevisionPlano(projectId: string, fileId: string, revisionId: string): Promise<void> {
  await apiFetch<void>(`${base(projectId, fileId)}/${revisionId}`, { method: 'DELETE', acceptStatus: [204] as number[] });
}
